import {Component, OnDestroy, OnInit, computed, inject, input, signal} from '@angular/core';
import {Observable, Subscription} from 'rxjs';
import {
  DiagnosisCode,
  MasterDataRepositoryService,
  PatientDiagnosisCode,
  Permissions,
  UserSettings
} from '@xmedical/core';
import {DiagnosisAddedEvent, DiagnosisDisplayItem, DiagnosisRemovedEvent} from '@xmedical/shared';
import {CoreService} from '../../../core/services/core.service';
import {PatientDiagnosisCodesStore, PatientStore, PracticeStore} from '../../../core/state';

@Component({
    selector: 'app-patient-diagnosis-code-widget',
    templateUrl: './diagnosis-code-widget.component.html',
    styleUrls: ['./diagnosis-code-widget.component.scss'],
    standalone: false
})
export class DiagnosisCodeWidgetComponent implements OnInit, OnDestroy {

  title = input<string>('Diagnosis Codes');

  settings: UserSettings;
  private subscriptions: Subscription[] = [];

  private masterDataRepository = inject(MasterDataRepositoryService);
  private coreService = inject(CoreService);
  private patientStore = inject(PatientStore);
  private practiceStore = inject(PracticeStore);
  protected diagnosisCodesStore = inject(PatientDiagnosisCodesStore);

  isProcessing = signal(false);

  canAdd = computed(() => this.coreService.hasPermission(Permissions.ClinicalDiagnosisCodesWrite));
  canDelete = computed(() => this.coreService.hasPermission(Permissions.ClinicalDiagnosisCodesDelete));

  diagnosisItems = computed<DiagnosisDisplayItem[]>(() =>
    this.diagnosisCodesStore.items().map(item => ({
      id: item.id,
      code: item.icD10Code,
      description: item.description ?? '',
      comment: item.comment,
      lastModifiedDate: item.lastModifiedDate,
      modifiedDateUtc: item.modifiedDateUtc,
      lastModifiedUser: item.lastModifiedUser
    }))
  );

  get patientId(): number {
    return this.patientStore.current()?.id ?? 0;
  }

  // ICD-10 codes are fixed master data — there is no per-practice list and no
  // "create new code" path, so search always goes to master data.
  searchDiagnosisCodes$ = (query: string): Observable<DiagnosisCode[]> => {
    return this.masterDataRepository.searchDiagnosisCodes(query);
  }

  ngOnInit(): void {
    this.settings = this.coreService.settings();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  onItemAdded(event: DiagnosisAddedEvent) {
    const now = new Date();
    const user = this.coreService.currentUser;

    // Optimistic row. `id` is 0 until the server hands back the real
    // patient-link id; the master code's id goes to the store separately
    // because that is what the add API keys off.
    const item: PatientDiagnosisCode = {
      id: 0,
      uid: '',
      practiceId: this.practiceStore.selectedPracticeId() ?? 0,
      patientId: this.patientId,
      icD10Code: event.item.code,
      description: event.item.description,
      comment: event.comment,
      createdUser: user,
      lastModifiedUser: user,
      createdDate: now,
      createdDateUtc: now,
      lastModifiedDate: now,
      modifiedDateUtc: now,
      deletedDate: null,
      deletedUser: null,
      isDeleted: false
    };

    this.isProcessing.set(true);
    this.subscriptions.push(this.diagnosisCodesStore.add(item, event.item.id).subscribe({
      next: () => this.isProcessing.set(false),
      // The store rolls the optimistic row back on failure, so without a toast
      // the code the user just picked would simply vanish with no explanation.
      error: () => {
        this.coreService.showErrorToast('Could not add diagnosis code to this patient');
        this.isProcessing.set(false);
      }
    }));
  }

  onItemRemoved(event: DiagnosisRemovedEvent) {
    const id = event.item.id;
    if (!id) return;
    this.isProcessing.set(true);
    this.subscriptions.push(this.diagnosisCodesStore.remove(id).subscribe({
      // A `false` here means the server responded but kept the row — the store
      // has already put it back, so say so rather than reporting success.
      next: deleted => {
        if (!deleted) {
          this.coreService.showErrorToast('Could not remove diagnosis code');
        }
        this.isProcessing.set(false);
      },
      error: () => {
        this.coreService.showErrorToast('Could not remove diagnosis code');
        this.isProcessing.set(false);
      }
    }));
  }
}
